<!-- SettingsForm.vue -->
<template>
  <v-card>
    <v-card-title class="settings-form-header text-h5">
      <span class="settings-form-header__title">시스템 설정</span>
      <div class="settings-form-header__actions">
        <v-btn color="primary" @click="submit">저장</v-btn>
        <v-btn color="primary" variant="text" @click="openImportDialog">가져오기</v-btn>
        <v-btn v-if="!setupRequired" color="primary" variant="text" @click.stop="storeCurrentPrompt">넣어두기</v-btn>
        <v-btn v-if="!setupRequired" variant="text" @click="$emit('close')">닫기</v-btn>
      </div>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-form ref="form">
        <v-tabs v-model="currentTab" color="primary" grow>
          <v-tab value="prompt">시스템 프롬프트 설정</v-tab>
          <v-tab value="params">파라미터 설정</v-tab>
          <v-tab value="forbidden">금지어 변환</v-tab>
        </v-tabs>
        <v-divider />
        <v-tabs-window v-model="currentTab">
          <v-tabs-window-item value="prompt">
            <v-container fluid>
              <v-alert
                color="primary"
                variant="tonal"
                density="compact"
                class="mb-3 text-body-2"
              >
                💡 <strong>solar-pro</strong> 선택 시 <strong>2027년 1월 31일까지 무료 프로모션</strong> 적용 (미적용시 건당 0.2크레딧)
              </v-alert>

              <v-combobox
                v-model="settings.model"
                :items="modelOptions"
                item-title="text"
                item-value="value"
                label="모델 선택"
                clearable
                :rules="[(v) => !!v || '모델을 선택하거나 입력하세요.']"
                variant="underlined"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template #append v-if="item.raw && item.raw.value === 'solar-pro3'">
                      <v-chip color="success" size="small" variant="flat" class="ml-2 font-weight-bold">2027-01-31까지 무료</v-chip>
                    </template>
                  </v-list-item>
                </template>
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiRobot" viewbox="0 0 24 24" />
                </template>
              </v-combobox>

              <v-combobox
                v-model="settings.task"
                :items="taskOptions"
                item-title="text"
                item-value="value"
                label="AI가 할 일 (AI가 작성해 줄 특기사항의 종류)"
                clearable
                :return-object="false"
                :rules="[
                  (v) => !!v || 'AI가 작성해 줄 것을 선택하거나 입력하세요.',
                  (v) => v.length <= 30 || '30자 이내로 입력하세요.',
                ]"
                maxlength="30"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiFormatListBulleted" viewbox="0 0 24 24" />
                </template>
              </v-combobox>

              <v-text-field
                v-model="settings.subjectField"
                label="과목 또는 분야 (창체: 자율활동, 동아리활동, 진로활동  |  행특: 협업과 소통능력, 나눔과 배려, 성실성과 규칙준수 등)"
                :rules="[
                  (v) => !!v || '교과목 또는 창체의 세부 분야를 입력하세요.',
                  (v) => v.length <= 30 || '30자 이내로 입력하세요.',
                ]"
                required
                maxlength="30"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiBookOpenVariant" viewbox="0 0 24 24" />
                </template>
              </v-text-field>

              <v-text-field
                v-model="settings.activityContent"
                label="학생들이 수행한 활동 내용 (선택사항)"
                :rules="[(v) => (v === '' || v.length <= 100) || '100자 이내로 입력하세요.']"
                maxlength="100"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiRunFast" viewbox="0 0 24 24" />
                </template>
              </v-text-field>

              <v-text-field
                v-model="settings.providedMaterials"
                label="학생별로 AI에게 제공할 자료의 제목이나 목록"
                :rules="[
                  (v) => !!v || '학생별로 AI에게 제공할 자료의 제목을 입력하세요.',
                  (v) => v.length <= 150 || '100자 이내로 입력하세요.',
                ]"
                required
                maxlength="150"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiFileDocumentOutline" viewbox="0 0 24 24" />
                </template>
              </v-text-field>

              <v-text-field
                v-model="settings.includedContent"
                label="특기사항에 포함할 내용 또는 역량 (선택사항)"
                :rules="[(v) => (v === '' || v.length <= 100) || '100자 이내로 입력하세요.']"
                maxlength="100"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiContentSaveOutline" viewbox="0 0 24 24" />
                </template>
              </v-text-field>

              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="settings.minBytes"
                    label="최소 바이트"
                    type="number"
                    :min="0"
                    :max="3000"
                    variant="underlined"
                    :rules="[(v) => (v !== null && v >= 0 && v <= 3000) || '0이상 3000이하의 값을 입력하세요.']"
                    required
                  >
                    <template #prepend-inner>
                      <svg-icon class="text-field-icon" :path="icons.mdiNumeric" viewbox="0 0 24 24" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="settings.maxBytes"
                    label="최대 바이트"
                    type="number"
                    :min="settings.minBytes"
                    :max="3000"
                    variant="underlined"
                    :rules="[(v) => (v !== null && v >= settings.minBytes && v <= 3000) || '최소 바이트 이상 3000이하의 값을 입력하세요.']"
                    required
                  >
                    <template #prepend-inner>
                      <svg-icon class="text-field-icon" :path="icons.mdiNumeric" viewbox="0 0 24 24" />
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-textarea
                v-model="settings.conditions"
                label="작성 조건"
                :rules="[(v) => (v === '' || v.length <= 1500) || '1,500자 이내로 입력하세요.']"
                rows="10"
                maxlength="1500"
                counter
                variant="underlined"
              >
                <template #prepend-inner>
                  <svg-icon class="text-field-icon" :path="icons.mdiTextBoxOutline" viewbox="0 0 24 24" />
                </template>
              </v-textarea>
            </v-container>
          </v-tabs-window-item>

          <v-tabs-window-item value="params">
            <OpenAIParameters
              v-model="settings.openAIParameters"
              :selected-model-value="typeof settings.model === 'object' ? settings.model.value : settings.model"
            />
          </v-tabs-window-item>

          <v-tabs-window-item value="forbidden">
            <ForbiddenWords v-model="settings.forbiddenWords" />
          </v-tabs-window-item>
        </v-tabs-window>
      </v-form>
    </v-card-text>
    <SystemPromptImportDialog v-model="showImportDialog" :user-id="userId" @applyPrompt="applyImportedPrompt" />
  </v-card>
</template>

<script>
import { VTabsWindow, VTabsWindowItem } from 'vuetify/components';
import OpenAIParameters from './OpenAIParameters.vue';
import ForbiddenWords from './ForbiddenWords.vue';
import SystemPromptImportDialog from './SystemPromptImportDialog.vue';
import icons from '@/icons';
import { getModelValue } from '@/utils/specialNoteHelpers';

export default {
  components: {
    VTabsWindow,
    VTabsWindowItem,
    OpenAIParameters,
    ForbiddenWords,
    SystemPromptImportDialog,
  },
  props: {
    userSettings: {
      type: Object,
      default: () => ({}),
    },
    userId: {
      type: String,
      required: true,
      default: '',
    },
    setupRequired: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submitSettings', 'openStorePromptDialog', 'settings-changed'],
  data() {
    return {
      icons,
      currentTab: 'prompt',
      defaultSettings: {
        model: {
          text: 'gpt',
          value: 'gpt-4.1',
        },
        task: '과목별 세부능력 및 특기사항',
        subjectField: '화학',
        activityContent: '자유탐구활동(자료 조사, 논문 분석, 탐구 실험 등)',
        providedMaterials: '학생이 수행한 탐구 방법, 제목 또는 논문 제목, 교사의 관찰 평가 문장, 학생의 자기평가 문장, 발표에 대한 동료평가 문장',
        includedContent: '학생이 수행한 노력과 활동, 학생의 장점과 발전 가능성, 진로 연관성 등',
        minBytes: 0,
        maxBytes: 1500,
        conditions: `## 작성 순서
1. 제목과 탐구 방법을 작성: 
   - 예시: ~~~을 제목으로 조사하여 발표함.
   - 예시: ~~~을 제목으로 실험하여 발표함.
2. 이 주제를 선택한 이유
3. 실제로 학생이 수행한 내용, 장점
4. 이 활동을 통해 배운 점(느낀 점)
5. 나머지 내용을 추가`,
        openAIParameters: {
          temperature: 0.2,
          topP: 0.8,
          frequencyPenalty: 0.8,
          presencePenalty: 1.3,
          seed: 0,
          randomSeed: true,
        },
        forbiddenWords: [{ id: 1, before: '한국고', after: 'OO고' }],
      },
      settings: {},
      showImportDialog: false,
      modelOptions: [
        { text: 'gpt', value: 'gpt-4.1' },
        { text: 'solar-pro', value: 'solar-pro3' },
      ],
      taskOptions: [
        { text: '과목별 세부능력 및 특기사항', value: '과목별 세부능력 및 특기사항' },
        { text: '창의적 체험활동 특기사항', value: '창의적 체험활동 특기사항' },
        { text: '행동특성 및 종합의견', value: '행동특성 및 종합의견' },
      ],
      suppressSettingsChanged: false,
    };
  },
  watch: {
    userSettings: {
      handler() {
        this.initializeSettings();
      },
      immediate: true,
      deep: true,
    },
    settings: {
      handler() {
        if (this.suppressSettingsChanged) {
          return;
        }
        this.$emit('settings-changed');
      },
      deep: true,
    },
  },
  methods: {
    async submit() {
      const validation = await this.$refs.form?.validate();
      if (validation && validation.valid === false) {
        return;
      }
      try {
        this.$emit('submitSettings', this.settings);
      } catch (error) {
        console.error('설정 저장 실패:', error);
      }
    },
    openImportDialog() {
      this.showImportDialog = true;
    },
    storeCurrentPrompt() {
      this.$emit('openStorePromptDialog');
    },
    applyImportedPrompt(importedSettings) {
      const nextSettings = { ...importedSettings };
      delete nextSettings.forbiddenWords;
      this.settings = { ...this.settings, ...nextSettings };
    },
    initializeSettings() {
      this.suppressSettingsChanged = true;
      if (this.userSettings && Object.keys(this.userSettings).length > 0) {
        const openAIParams = {
          ...this.defaultSettings.openAIParameters,
          ...(this.userSettings.openAIParameters || {}),
        };
        this.settings = {
          ...this.defaultSettings,
          ...this.userSettings,
          model: getModelValue(this.userSettings.model)
            ? this.userSettings.model
            : this.defaultSettings.model,
          openAIParameters: openAIParams,
        };

        if (
          typeof this.userSettings.openAIParameters?.seed === 'undefined' ||
          typeof this.userSettings.openAIParameters?.randomSeed === 'undefined'
        ) {
          this.submit();
        }
      } else {
        this.settings = { ...this.defaultSettings };
      }

      this.$nextTick(() => {
        this.suppressSettingsChanged = false;
      });
    },
  },
};
</script>

<style scoped>
.settings-form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: nowrap;
  padding: 16px 16px 12px;
  min-height: 64px;
}

.settings-form-header__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.5rem;
  font-weight: 500;
  white-space: nowrap;
}

.settings-form-header__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  gap: 4px;
  flex-wrap: nowrap;
}

.settings-form-header__actions :deep(.v-btn) {
  margin-left: 0;
  min-width: auto;
}

@media (max-width: 720px) {
  .settings-form-header {
    gap: 8px;
  }

  .settings-form-header__title {
    font-size: 1.3rem;
  }

  .settings-form-header__actions {
    gap: 2px;
  }

  .settings-form-header__actions :deep(.v-btn) {
    padding-inline: 8px;
  }
}

.text-field-icon {
  opacity: 0.7;
}
</style>

